import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import type {
  EngineHealthEntry,
  EngineHealthTenantEntry,
  EnginesHealthData,
  ResponsePerEngineData,
} from "@nexus/widgets";
import type { DataResolver } from "@/lib/dashboard/resolverTypes";
import { getTenantIds } from "./shared";

// ── Engines Health ────────────────────────────────────────────────────────────

const ENGINES_HEALTH_QUERY = gql`
  query EnginesHealthcheck($tenant_ids: [Int!], $grouped: Boolean) {
    get_engines_healthcheck(tenant_ids: $tenant_ids, grouped: $grouped) {
      engine_id
      engine_label
      tenant_id
      tenant_label
      customer
      healthcheck_status
      latest_data
      monitoring_dashboard_link
      business_dashboard_link
      tenants {
        tenant_id
        tenant_label
        customer
        healthcheck_status
        latest_data
      }
      problem_tenants
    }
  }
`;

type EnginesHealthResponse = {
  get_engines_healthcheck: Array<{
    engine_id: number;
    engine_label: string;
    tenant_id: number;
    tenant_label: string;
    customer: string | null;
    healthcheck_status: boolean;
    latest_data: string;
    monitoring_dashboard_link: string;
    business_dashboard_link: string;
    tenants?: Array<{
      tenant_id: number;
      tenant_label: string;
      customer: string | null;
      healthcheck_status: boolean;
      latest_data: string;
    }>;
    problem_tenants?: Array<{
      tenant_id: number;
      tenant_label: string;
      customer: string | null;
      latest_data: string;
    }>;
  }>;
};

function normalizeEnginesHealth(raw: unknown): EnginesHealthData {
  const data = raw as EnginesHealthResponse;
  return {
    engines: (data.get_engines_healthcheck ?? []).map(
      (engine) =>
        ({
          engine_id: Number(engine.engine_id),
          engine_label: engine.engine_label,
          tenant_id: Number(engine.tenant_id),
          tenant_label: engine.tenant_label,
          customer: engine.customer,
          healthcheck_status: Boolean(engine.healthcheck_status),
          latest_data: engine.latest_data,
          monitoring_dashboard_link: engine.monitoring_dashboard_link,
          business_dashboard_link: engine.business_dashboard_link,
          tenants: (engine.tenants ?? []).map((tenant): EngineHealthTenantEntry => ({
            tenant_id: Number(tenant.tenant_id),
            tenant_label: tenant.tenant_label,
            customer: tenant.customer,
            healthcheck_status: Boolean(tenant.healthcheck_status),
            latest_data: tenant.latest_data,
          })),
          problem_tenants: (engine.problem_tenants ?? []).map(
            (tenant): EngineHealthTenantEntry => ({
              tenant_id: Number(tenant.tenant_id),
              tenant_label: tenant.tenant_label,
              customer: tenant.customer,
              healthcheck_status: false,
              latest_data: tenant.latest_data,
            }),
          ),
        }) satisfies EngineHealthEntry,
    ),
  };
}

// ── Response per Engine ───────────────────────────────────────────────────────

const RESPONSE_PER_ENGINE_QUERY = gql`
  query ResponsePerEngine($from_ts: String!, $to_ts: String!, $tenant_ids: [Int!], $limit: Int) {
    response_per_engine(from_ts: $from_ts, to_ts: $to_ts, tenant_ids: $tenant_ids, limit: $limit) {
      engine_id
      engine_label
      total_actionable_infringements
      confirmed_count
      pending_count
      failed_count
      cancelled_count
      confirmed_rate
      pending_rate
      failed_rate
      cancelled_rate
    }
  }
`;

type ResponsePerEngineResponse = {
  response_per_engine: Array<{
    engine_id: number;
    engine_label: string;
    total_actionable_infringements: number;
    confirmed_count: number;
    pending_count: number;
    failed_count: number;
    cancelled_count: number;
    confirmed_rate: number;
    pending_rate: number;
    failed_rate: number;
    cancelled_rate: number;
  }>;
};

function normalizeResponsePerEngine(raw: unknown): ResponsePerEngineData {
  const data = raw as ResponsePerEngineResponse;
  return {
    engines: (data.response_per_engine ?? []).map((e) => ({
      engine_id: Number(e.engine_id),
      engine_label: e.engine_label,
      total_actionable_infringements: Number(e.total_actionable_infringements),
      confirmed_count: Number(e.confirmed_count),
      pending_count: Number(e.pending_count),
      failed_count: Number(e.failed_count),
      cancelled_count: Number(e.cancelled_count),
      confirmed_rate: Number(e.confirmed_rate),
      pending_rate: Number(e.pending_rate),
      failed_rate: Number(e.failed_rate),
      cancelled_rate: Number(e.cancelled_rate),
    })),
  };
}

// ── Resolvers ─────────────────────────────────────────────────────────────────

export const enginesDataSources: Record<string, DataResolver> = {
  "engines-health": {
    contract: { name: "engines-health", version: 1 },
    buildParams: (_requestParams, filters) => ({
      tenant_ids: getTenantIds(filters),
      grouped: Boolean(_requestParams.grouped ?? true),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<EnginesHealthResponse>(ENGINES_HEALTH_QUERY, params);
      return normalizeEnginesHealth(raw);
    },
  },

  "response-per-engine": {
    contract: { name: "response-per-engine", version: 1 },
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<ResponsePerEngineResponse>(
        RESPONSE_PER_ENGINE_QUERY,
        params,
      );
      return normalizeResponsePerEngine(raw);
    },
  },
};
